import {
  Button,
  Flex,
  Heading,
  IconButton,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import Paginator from '../../../components/paginator'
import { UserService } from '../../../services/User/UserService'
import { UserCreateModal } from './UserCreateModal'
import { UserRestrictDialog } from './UserRestrictDialog'
import { UserSetRolesModal } from './UserSetRolesModal'
import { UserDeleteDialog } from './UserDeleteDialog'
import { UserEditModal } from './UserEditModal'

export default function UsersPage() {
  const userService = new UserService()
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [users, setUsers] = useState<any[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isUserCreateModalOpen, setIsUserCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [modalUser, setModalUser] = useState<any>({ roles: [] })
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([])
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenDeleteDialog, onOpen: onOpenDeleteDialog, onClose: onCloseDeleteDialog } = useDisclosure()
  const [restrict, setRestrict] = useState(false)
  const cancelRef = useRef()

  const handleCloseModal = () => {
    setIsModalOpen(false)
    loadUsers(page)
  }

  const loadUsers = async (page: number) => {
    const { total, per_page, data } = await userService.list({ page })

    setUsers(data)

    setTotalPages(Math.ceil(total / per_page))
  }

  useEffect(() => {
    loadUsers(page)
    userService.listRoles().then((response) => {
      setRoles(response)
    })
  }, [])

  const handleOpenModal = (user: any) => {
    setModalUser(user)
    setIsModalOpen(true)
  }

  const handleCloseUserCreateModal = () => {
    setIsUserCreateModalOpen(false)
    loadUsers(page)
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
    loadUsers(page)
  }

  const onCloseDialog = () => {
    onClose()
    onCloseDeleteDialog()
    loadUsers(page)
  }

  return (
    <>
      <Heading as="h1" size="lg" marginBottom="5" mb="10" textTransform="uppercase">
        Usuários
      </Heading>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Nome</Th>
              <Th>E-mail</Th>
              <Th>Grupos</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user.id}>
                <Td>{user.id}</Td>
                <Td>
                  <div className="d-flex align-items-center">
                    {user.picture && (
                      <Image
                        src={`data:image/png;base64, ${user.picture}`}
                        alt={user.name}
                        width="30px"
                        height="30px"
                        marginEnd={2}
                      />
                    )}
                    {user.name}
                    {user.restricted && <span className="badge bg-danger ms-auto">Acesso restrito</span>}
                  </div>
                </Td>
                <Td>
                  <Flex align="center">
                    {user.email}
                    {user.login_method == 'google' && (
                      <>
                        <Tooltip label="Acesso pelo Google" hasArrow placement="top">
                          <Image src="/img/google-icon.png" alt="Google" width="20px" height="20px" ml={2} />
                        </Tooltip>
                      </>
                    )}
                  </Flex>
                </Td>
                <Td>
                  {user.pending_approval && <span className="badge bg-warning">Pendente</span>}
                  {user.roles.map((role: string) => (
                    <span key={role} className="badge bg-dark me-1">
                      {role}
                    </span>
                  ))}
                </Td>
                <Td>
                  {user.login_method == 'google' && (
                    <>
                      {user.pending_approval && (
                        <Tooltip label="Aprovar acesso" hasArrow placement="top">
                          <IconButton
                            aria-label="Aprovar acesso"
                            colorScheme="abraGreen"
                            size="sm"
                            mr={2}
                            onClick={() => handleOpenModal(user)}
                          >
                            <i className="lni lni-checkmark"></i>
                          </IconButton>
                        </Tooltip>
                      )}
                      {!user.pending_approval && (
                        <Tooltip label="Editar" hasArrow placement="top">
                          <IconButton
                            aria-label="Editar"
                            colorScheme="blue"
                            size="sm"
                            mr={2}
                            onClick={() => handleOpenModal(user)}
                          >
                            <i className="lni lni-pencil"></i>
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  {user.login_method == 'email' && (
                    <Tooltip label="Editar" hasArrow placement="top">
                      <IconButton
                        aria-label="Editar"
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setModalUser(user)
                          setIsEditModalOpen(true)
                        }}
                      >
                        <i className="lni lni-pencil"></i>
                      </IconButton>
                    </Tooltip>
                  )}
                  {!user.pending_approval && (
                    <>
                      {!user.restricted && (
                        <Tooltip label="Restringir acesso" hasArrow placement="top">
                          <IconButton
                            aria-label="Restringir acesso"
                            colorScheme="yellow"
                            size="sm"
                            mr={2}
                            onClick={() => {
                              setModalUser(user)
                              setRestrict(true)
                              onOpen()
                            }}
                          >
                            <i className="lni lni-lock-alt"></i>
                          </IconButton>
                        </Tooltip>
                      )}
                      {user.restricted && (
                        <Tooltip label="Liberar acesso" hasArrow placement="top">
                          <IconButton
                            aria-label="Liberar acesso"
                            colorScheme="abraGreen"
                            size="sm"
                            mr={2}
                            onClick={() => {
                              setModalUser(user)
                              setRestrict(false)
                              onOpen()
                            }}
                          >
                            <i className="lni lni-unlock"></i>
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  <Tooltip label="Excluir" hasArrow placement="top">
                    <IconButton
                      aria-label="Excluir"
                      colorScheme="red"
                      size="sm"
                      mr={2}
                      onClick={() => {
                        setModalUser(user)
                        onOpenDeleteDialog()
                      }}
                    >
                      <i className="lni lni-trash-can"></i>
                    </IconButton>
                  </Tooltip>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
          loadUsers(page)
        }}
      />
      <UserSetRolesModal isOpen={isModalOpen} onClose={handleCloseModal} user={modalUser} roles={roles} />
      <UserCreateModal isOpen={isUserCreateModalOpen} onClose={handleCloseUserCreateModal} roles={roles} />
      <UserEditModal isOpen={isEditModalOpen} onClose={handleCloseEditModal} user={modalUser} roles={roles} />
      <UserRestrictDialog
        isOpen={isOpen}
        onClose={onCloseDialog}
        user={modalUser}
        restrict={restrict}
        leastDestructiveRef={cancelRef}
      />
      <UserDeleteDialog
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDialog}
        user={modalUser}
        leastDestructiveRef={cancelRef}
      />

      <Button
        position="fixed"
        bottom="5"
        right="5"
        size="lg"
        colorScheme="magenta"
        borderRadius="full"
        boxShadow="lg"
        width={16}
        height={16}
        onClick={() => setIsUserCreateModalOpen(true)}
      >
        <i className="lni lni-plus"></i>
      </Button>
    </>
  )
}
